/**
 * Integrytas Group - API Performance Guardrail (APG)
 * Page: APG.TELEMETRY
 *
 * Slow-call telemetry analyzer: the aggregate pattern ledger (hotspots by
 * occurrence/duration, triageable), recent verbatim violations with captured
 * params/context, per-endpoint threshold overrides, and (if installed) the top
 * pg_stat_statements queries.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate, routedDelete } from '../../lib/routed_writes';
import type { AuthSession } from '../../types/auth';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'APG.TELEMETRY';

interface PatternRow { apg_pattern_id: number; fingerprint: string; request_kind: string | null; endpoint: string | null; measured_class: string | null; occurrences: number; max_duration_ms: number; sum_duration_ms: number; last_duration_ms: number | null; last_seen: string; status: string; notes: string | null }
interface ViolRow { apg_violation_id: number; request_kind: string | null; endpoint: string | null; measured_class: string | null; duration_ms: number; threshold_ms: number | null; params: unknown; screen_code: string | null; une_workspace_id: number | null; user_id: number | null; created_at: string }
interface ThreshRow { apg_threshold_id: number; measured_class: string; match_type: string; match_value: string; threshold_ms: number; is_active: 'Y' | 'N' }

const PATTERN_GRID_COLS: GridColumnDef[] = [
  { key: 'endpoint',       header: 'Endpoint', defaultWidth: 280 },
  { key: 'measured_class', header: 'Class',    defaultWidth: 100 },
  { key: 'occurrences',    header: 'Hits',     defaultWidth: 80  },
  { key: 'max_duration_ms',header: 'Max ms',   defaultWidth: 90  },
  { key: 'avg_ms',         header: 'Avg ms',   defaultWidth: 90  },
  { key: 'last_seen',      header: 'Last',     defaultWidth: 170 },
  { key: 'status',         header: 'Status',   defaultWidth: 150 },
];

/** Numeric grid columns rendered right-aligned with tabular figures. */
const PATTERN_NUM_COLS = new Set(['occurrences', 'max_duration_ms', 'avg_ms']);

export interface ApgTelemetryPageProps { session: AuthSession }

export function ApgTelemetryPage({ session }: ApgTelemetryPageProps) {
  return (
    <PersonalizationProvider pageCode="APG.TELEMETRY" session={session}>
      <ApgTelemetryPageInner session={session} />
    </PersonalizationProvider>
  );
}

function ApgTelemetryPageInner({ session }: ApgTelemetryPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canDelete = hasAccess(session, PAGE_CODE, 'delete');

  const { t } = useTranslation();
  const tr = t;
  const [patterns, setPatterns] = useState<PatternRow[]>([]);
  const [viols, setViols] = useState<ViolRow[]>([]);
  const [thresh, setThresh] = useState<ThreshRow[]>([]);
  const [topSql, setTopSql] = useState<unknown[] | null>(null);
  const [userNames, setUserNames] = useState<Record<number, string>>({});
  const [open, setOpen] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // §17.B12: default the threshold-overrides list to Active-only.
  const [threshFacets, setThreshFacets] = useState<{ active: string[] }>({ active: ['Y'] });
  const detail = useExpandedRows<number>();

  // §17.B13 related children (expand/contract). Slow-call patterns are aggregates
  // with no per-row child collection loaded (violations carry no fingerprint FK to
  // roll back up), so the control renders inert.
  // TODO: wire relations
  const buildRelatedGroups = (_id: number): RelatedChildGroup[] => [];
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const [p, v, t] = await Promise.all([
        apiDb.from('apg_pattern').select('apg_pattern_id, fingerprint, request_kind, endpoint, measured_class, occurrences, max_duration_ms, sum_duration_ms, last_duration_ms, last_seen, status, notes').order('occurrences', { ascending: false }).limit(100),
        apiDb.from('apg_violation').select('apg_violation_id, request_kind, endpoint, measured_class, duration_ms, threshold_ms, params, screen_code, une_workspace_id, user_id, created_at').order('created_at', { ascending: false }).limit(50),
        apiDb.from('apg_threshold').select('apg_threshold_id, measured_class, match_type, match_value, threshold_ms, is_active').order('apg_threshold_id'),
      ]);
      if (p.error) throw p.error;
      const violRows = (v.data ?? []) as ViolRow[];
      setPatterns((p.data ?? []) as PatternRow[]); setViols(violRows); setThresh((t.data ?? []) as ThreshRow[]);
      // Resolve the violators' surrogate user_ids to display names (§17.B4).
      const ids = Array.from(new Set(violRows.map(x => x.user_id).filter((x): x is number => x != null)));
      if (ids.length) {
        const { data: us } = await apiDb.from('csm_user_master').select('user_id, display_name, email').in('user_id', ids);
        const m: Record<number, string> = {};
        for (const u of (us ?? []) as { user_id: number; display_name: string | null; email: string }[]) m[u.user_id] = u.display_name || u.email || '—';
        setUserNames(m);
      } else { setUserNames({}); }
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => { void reload(); }, []);

  const savePattern = async (p: PatternRow) => { await routedUpdate(session, 'apg_pattern', 'apg_pattern_id', p.apg_pattern_id, { status: p.status, notes: p.notes }); };
  const addThresh = async () => { await routedInsert(session, 'apg_threshold', { measured_class: 'QUERY', match_type: 'RPC', match_value: '', threshold_ms: 1000, is_active: 'Y' }); await reload(); };
  const saveThresh = async (t: ThreshRow) => { await routedUpdate(session, 'apg_threshold', 'apg_threshold_id', t.apg_threshold_id, { measured_class: t.measured_class, match_type: t.match_type, match_value: t.match_value, threshold_ms: t.threshold_ms, is_active: t.is_active }); };
  const delThresh = async (id: number) => { await routedDelete(session, 'apg_threshold', 'apg_threshold_id', id); setThresh(ts => ts.filter(x => x.apg_threshold_id !== id)); };
  // Edit inline by stable id (the list is filtered, so positional indexes are unreliable).
  const patchThresh = (id: number, patch: Partial<ThreshRow>) => setThresh(ts => ts.map(x => x.apg_threshold_id === id ? { ...x, ...patch } : x));
  const saveThreshById = (id: number) => { const row = thresh.find(x => x.apg_threshold_id === id); if (row) void saveThresh(row); };

  const loadTopSql = async () => {
    setBusy(true); setError(null); setNotice(null);
    try {
      const { data } = await apiDb.rpc('apg_top_statements', { p_limit: 20 });
      const r = data as { ok: boolean; statements?: unknown[]; error_code?: string };
      if (!r?.ok) { setNotice(r?.error_code === 'APG.PG_STAT_STATEMENTS_NOT_INSTALLED' ? t('apg.pg_stat_not_installed', 'pg_stat_statements is not installed (optional tier).') : (r?.error_code ?? t('apg.unavailable', 'unavailable'))); setTopSql(null); return; }
      setTopSql(r.statements ?? []);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  // §17.B5/B12: Active facet for the threshold-overrides list (localized labels).
  const threshFacetOptions = useMemo(() => ({
    active: [
      { value: 'Y', label: t('common.boolean.active', 'Active') },
      { value: 'N', label: t('common.boolean.inactive', 'Inactive') },
    ],
  }), [t]);
  const threshFilterActive = threshFacets.active.length > 0;
  const clearThreshFilters = () => setThreshFacets({ active: [] });

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="csm-page-title">{t('apg.title', 'Performance Telemetry')}</span>
        <button onClick={() => void reload()} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{t('apg.refresh', 'Refresh')}</button>
        <button onClick={() => void loadTopSql()} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{t('apg.top_sql', 'Top SQL (pg_stat)')}</button>
        {busy && <span className="text-csm-xs text-csm-watermark">{t('apg.working', 'Working…')}</span>}
        {notice && <span className="text-csm-xs text-primary">{notice}</span>}
        <div className="ml-auto"><PersonalizationToggle /></div>
      </div>
      <div className="flex-1 overflow-auto p-3">
        {error && (
          <ErrorBanner
            message={t('apg.load_failed', "Couldn't load performance telemetry.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}
        <div className="mb-4 rounded border border-csm-border bg-white p-3">
          <div className="mb-2 text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('apg.bottleneck_patterns', 'Bottleneck Patterns')}</div>
          <PersonalizedGrid componentKey="grid" columns={PATTERN_GRID_COLS}>
            {(visibleCols, widths) => (
              <table className={GRID_TABLE_CLASS}>
                <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                  <tr>
                    <th className="w-7 border-b border-csm-border px-2 py-1 text-left" title={t('apg.related.header', 'Related records')} />
                    {visibleCols.map(col => (
                      <th key={col.key} className={`border-b border-csm-border px-2 py-1 text-left${PATTERN_NUM_COLS.has(col.key) ? ' csm-num' : ''}`}
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {patterns.map((p, i) => (
                    <Fragment key={p.apg_pattern_id}>
                    <tr className="hover:bg-csm-surface-alt">
                      <td className="border-b border-csm-border-soft px-2 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                        <RelatedExpandButton open={detail.isOpen(p.apg_pattern_id)} hasChildren={hasRelated(p.apg_pattern_id)} onToggle={() => detail.toggle(p.apg_pattern_id)} expandLabel={t('common.related.expand', 'Show related records')} collapseLabel={t('common.related.collapse', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                      </td>
                      {visibleCols.map(col => (
                        <td key={col.key} className={`border-b border-csm-border-soft px-2 py-1${PATTERN_NUM_COLS.has(col.key) ? ' csm-num' : ''}`}
                            style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                          {col.key === 'endpoint'        && <span className="font-mono text-csm-xs">{p.request_kind}:{p.endpoint ?? '—'}</span>}
                          {col.key === 'measured_class'  && <span className="text-csm-2xs">{p.measured_class}</span>}
                          {col.key === 'occurrences'     && <span className="text-csm-xs">{p.occurrences}</span>}
                          {col.key === 'max_duration_ms' && <span className="text-csm-xs">{Math.round(p.max_duration_ms)}</span>}
                          {col.key === 'avg_ms'          && <span className="text-csm-xs">{p.occurrences ? Math.round(p.sum_duration_ms / p.occurrences) : 0}</span>}
                          {col.key === 'last_seen'       && <span className="text-csm-2xs text-csm-watermark">{new Date(p.last_seen).toLocaleString()}</span>}
                          {col.key === 'status'          && (
                            <select value={p.status} onChange={e => { const v = e.target.value; setPatterns(ps => ps.map((x, j) => j === i ? { ...x, status: v } : x)); void savePattern({ ...patterns[i], status: v }); }} className="h-6 rounded border border-csm-border px-1 text-csm-2xs">
                              {['OPEN', 'ACKNOWLEDGED', 'RESOLVED'].map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                          )}
                        </td>
                      ))}
                    </tr>
                    {detail.isOpen(p.apg_pattern_id) && (
                      <tr><td colSpan={visibleCols.length + 1} className="border-b border-csm-border-soft bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}><RelatedChildrenPanel groups={buildRelatedGroups(p.apg_pattern_id)} session={session} /></td></tr>
                    )}
                    </Fragment>
                  ))}
                  {patterns.length === 0 && !busy && <tr><td colSpan={visibleCols.length + 1} className="px-2 py-3"><EmptyState variant="all-clear" title={t('apg.no_patterns', 'No slow-call patterns recorded.')} /></td></tr>}
                </tbody>
              </table>
            )}
          </PersonalizedGrid>
        </div>

        {topSql && (
          <div className="mb-4 rounded border border-csm-border bg-white p-3">
            <div className="mb-2 text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('apg.top_pg_stat', 'Top pg_stat_statements (by mean time)')}</div>
            <pre className="max-h-64 overflow-auto font-mono text-csm-2xs text-csm-ink-soft">{JSON.stringify(topSql, null, 2)}</pre>
          </div>
        )}

        <div className="mb-4 rounded border border-csm-border bg-white p-3">
          <div className="mb-2 text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('apg.recent_violations', 'Recent Violations')}</div>
          <table className="min-w-full border-collapse text-csm-md">
            <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
              <tr><th className="border-b border-csm-border px-2 py-1 text-left">{t('apg.col.when', 'When')}</th><th className="border-b border-csm-border px-2 py-1 text-left">{t('apg.col.endpoint', 'Endpoint')}</th><th className="csm-num border-b border-csm-border px-2 py-1 text-right">{t('apg.col.ms', 'ms')}</th><th className="border-b border-csm-border px-2 py-1 text-left">{t('apg.col.screen', 'Screen')}</th><th className="border-b border-csm-border px-2 py-1 text-left">{t('apg.col.user', 'User')}</th><th className="border-b border-csm-border px-2 py-1 text-right">{t('apg.col.params', 'Params')}</th></tr>
            </thead>
            <tbody>
              {viols.map(v => (
                <Fragment key={v.apg_violation_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-2 py-1 text-csm-2xs text-csm-watermark">{new Date(v.created_at).toLocaleString()}</td>
                    <td className="border-b border-csm-border-soft px-2 py-1 font-mono text-csm-xs">{v.request_kind}:{v.endpoint ?? '—'}</td>
                    <td className="csm-num border-b border-csm-border-soft px-2 py-1 text-right text-csm-xs text-csm-danger">{Math.round(v.duration_ms)}</td>
                    <td className="border-b border-csm-border-soft px-2 py-1 font-mono text-csm-2xs">{v.screen_code ?? '—'}{/* fk-display-ok: raw perf-telemetry screen tag, not a FK to a named entity */}</td>
                    <td className="border-b border-csm-border-soft px-2 py-1 text-csm-2xs">{v.user_id == null ? '—' : (userNames[v.user_id] ?? '—')}</td>
                    <td className="border-b border-csm-border-soft px-2 py-1 text-right"><button onClick={() => setOpen(open === v.apg_violation_id ? null : v.apg_violation_id)} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-2xs hover:bg-csm-surface-alt">{open === v.apg_violation_id ? t('apg.hide', 'Hide') : t('apg.view', 'View')}</button></td>
                  </tr>
                  {open === v.apg_violation_id && (
                    <tr><td colSpan={6} className="border-b border-csm-border-soft bg-csm-surface-alt px-2 py-1"><pre className="max-h-48 overflow-auto font-mono text-csm-2xs text-csm-ink-soft">{JSON.stringify(v.params, null, 2)}</pre></td></tr>
                  )}
                </Fragment>
              ))}
              {viols.length === 0 && !busy && <tr><td colSpan={6} className="px-2 py-3"><EmptyState variant="all-clear" title={t('apg.no_violations', 'No violations.')} /></td></tr>}
            </tbody>
          </table>
        </div>

        <div className="rounded border border-csm-border bg-white p-3">
          <div className="mb-2 flex items-center gap-2">
            <span className="text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('apg.threshold_overrides', 'Threshold Overrides')}</span>
            {canAdd && (<button onClick={() => void addThresh()} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-surface-alt">{t('apg.add', '+ Add')}</button>)}
            <span className="text-csm-2xs text-csm-watermark">{t('apg.globals_note', 'globals: API 2000ms / QUERY 500ms (system params)')}</span>
            <div className="ml-auto flex items-center gap-2">
              <FacetFilter label={t('common.boolean.active', 'Active')} options={threshFacetOptions.active} selected={threshFacets.active} onChange={v => setThreshFacets(f => ({ ...f, active: v }))} />
              <ClearFiltersButton active={threshFilterActive} onClear={clearThreshFilters} label={t('common.clear_filters', 'Clear filters')} />
            </div>
          </div>
          {thresh.filter(row => threshFacets.active.length === 0 || threshFacets.active.includes(row.is_active)).map(t => (
            <div key={t.apg_threshold_id} className="mb-1 flex flex-wrap items-center gap-1">
              <select value={t.measured_class} onChange={e => patchThresh(t.apg_threshold_id, { measured_class: e.target.value })} onBlur={() => saveThreshById(t.apg_threshold_id)} className="h-7 rounded border border-csm-border px-1 text-csm-2xs">{['API', 'QUERY'].map(c => <option key={c} value={c}>{c}</option>)}</select>
              <select value={t.match_type} onChange={e => patchThresh(t.apg_threshold_id, { match_type: e.target.value })} onBlur={() => saveThreshById(t.apg_threshold_id)} className="h-7 rounded border border-csm-border px-1 text-csm-2xs">{['RPC', 'TABLE', 'KIND'].map(m => <option key={m} value={m}>{m}</option>)}</select>
              <input value={t.match_value} onChange={e => patchThresh(t.apg_threshold_id, { match_value: e.target.value })} onBlur={() => saveThreshById(t.apg_threshold_id)} placeholder={tr('apg.match_value_placeholder', 'rpc / table / kind')} className="h-7 w-40 rounded border border-csm-border px-1 font-mono text-csm-2xs" />
              <input type="number" value={t.threshold_ms} onChange={e => patchThresh(t.apg_threshold_id, { threshold_ms: Number(e.target.value) })} onBlur={() => saveThreshById(t.apg_threshold_id)} className="h-7 w-20 rounded border border-csm-border px-1 text-csm-2xs" title={tr('apg.ms_title', 'ms')} />
              <label className="flex items-center gap-1 text-csm-2xs"><input type="checkbox" checked={t.is_active === 'Y'} onChange={e => patchThresh(t.apg_threshold_id, { is_active: e.target.checked ? 'Y' : 'N' })} onBlur={() => saveThreshById(t.apg_threshold_id)} /> {tr('apg.on', 'on')}</label>
              {canDelete && (<button onClick={() => void delThresh(t.apg_threshold_id)} className="text-csm-danger text-csm-2xs">{tr('apg.del', 'del')}</button>)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
